import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { scrollToId } from 'shared/lib/scrollTo';
import CustomButton from 'shared/components/buttons/Custom';
import styles from './Collapsable.module.scss';

const Collapsable = ({
  maxHeight,
  collapseTitle: collapseTitleProp,
  expandTitle: expandTitleProp,
  enabled,
  className,
  idToScrollOnCollapse,
  children,
  noDivider,
  onExpand,
  onCollapse
}) => {
  const { t } = useTranslation('common');
  const content = useRef(null);
  const [collapsable, setCollapsable] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapse = () => {
    if (!collapsed && idToScrollOnCollapse) {
      scrollToId(idToScrollOnCollapse);
    }

    if (collapsed) {
      onExpand();
    } else {
      onCollapse();
    }

    setCollapsed(prev => !prev);
  };

  useEffect(() => {
    if (!content.current) {
      return;
    }

    if (enabled && content.current.clientHeight > maxHeight) {
      setCollapsable(true);
    } else if (!enabled) {
      setCollapsable(false);
    }
  }, [enabled, maxHeight]);

  const collapseTitle = collapseTitleProp || t('linkButton.collapse');
  const expandTitle = expandTitleProp || t('linkButton.expand');

  return (
    <div className={cn(styles.collapsable, className)}>
      <div
        className={cn(styles.viewbox, {
          [styles.collapsed]: collapsable && collapsed,
          [styles.noDivider]: noDivider,
        })}
        style={{ maxHeight: collapsable && collapsed ? maxHeight : undefined }}
      >
        <div className={styles.content} ref={content}>{children}</div>
      </div>
      {collapsable && (
        <div className={styles.controls}>
          <CustomButton className={styles.button} iconClassName={`la la-angle-double-${collapsed ? 'down' : 'up'}`} onClick={toggleCollapse}>
            {collapsed ? expandTitle : collapseTitle}
          </CustomButton>
        </div>
      )}
    </div>
  );
};

Collapsable.propTypes = {
  idToScrollOnCollapse: PropTypes.string,
  maxHeight: PropTypes.number.isRequired,
  collapseTitle: PropTypes.string,
  expandTitle: PropTypes.string,
  enabled: PropTypes.bool,
  className: PropTypes.string,
  noDivider: PropTypes.bool,
  onExpand: PropTypes.func,
  onCollapse: PropTypes.func,
};

Collapsable.defaultProps = {
  idToScrollOnCollapse: null,
  collapseTitle: null,
  expandTitle: null,
  enabled: true,
  className: null,
  noDivider: false,
  onExpand() {},
  onCollapse() {},
};

export default Collapsable;
